import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

interface TechnicalAnalysisResponse {
  question: string;
  technicalExplanation: string;
  algorithmDetails: string;
  llmPrompt: string;
  codeReferences: string[];
  proposedExtraAIInstructions?: string;
  affectedConstraintId?: number;
}

@Component({
  selector: 'app-technical-analysis-dialog',
  templateUrl: './technical-analysis-dialog.component.html',
  styleUrls: ['./technical-analysis-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatCardModule,
  ],
})
export class TechnicalAnalysisDialogComponent implements OnInit {
  question: string = '';
  loading: boolean = false;
  analysis: TechnicalAnalysisResponse | null = null;
  error: string | null = null;
  acceptingInstructions: boolean = false;

  private apiBaseUrl = environment.apiBaseUrl;

  constructor(
    public dialogRef: MatDialogRef<TechnicalAnalysisDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { scheduleId: number; versionNumber: number },
    private http: HttpClient
  ) {}

  ngOnInit() {
    console.log('Technical Analysis Dialog initialized with data:', this.data);
    // Try to restore the last used technical question from localStorage
    const lastTechnicalQuestion = localStorage.getItem('lastTechnicalAnalysisQuestion');
    if (lastTechnicalQuestion) {
      this.question = lastTechnicalQuestion;
    } else if (!this.question) {
      this.question = "Why is the solver taking so long to find a solution?";
    }
  }

  analyzeTechnical() {
    if (!this.question.trim()) return;

    // Store the technical question in localStorage before analyzing
    localStorage.setItem('lastTechnicalAnalysisQuestion', this.question.trim());

    this.loading = true;
    this.error = null;

    this.http.post<TechnicalAnalysisResponse>(
      `${this.apiBaseUrl}/schedules/${this.data.scheduleId}/versions/${this.data.versionNumber}/analyze/technical`,
      { question: this.question.trim() }
    ).subscribe({
      next: (result) => {
        console.log('Technical analysis result:', result);
        console.log('Has proposed instructions?', !!result.proposedExtraAIInstructions);
        console.log('Affected constraint ID:', result.affectedConstraintId);
        this.analysis = result;
        this.loading = false;
      },
      error: (err) => {
        console.error('Technical analysis error:', err);
        this.error = err.error?.error || 'Failed to analyze schedule. Please try again.';
        this.loading = false;
      }
    });
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
  }

  resetAnalysis() {
    this.analysis = null;
    this.error = null;
    this.question = '';
  }

  acceptProposedInstructions() {
    if (!this.analysis?.proposedExtraAIInstructions || !this.analysis?.affectedConstraintId) {
      return;
    }

    this.acceptingInstructions = true;
    this.error = null;

    this.http.post(
      `${this.apiBaseUrl}/schedules/${this.data.scheduleId}/constraints/${this.analysis.affectedConstraintId}/accept-ai-instructions`,
      { extraAIInstructions: this.analysis.proposedExtraAIInstructions }
    ).subscribe({
      next: () => {
        this.acceptingInstructions = false;
        // Close dialog and signal success
        this.dialogRef.close({ instructionsAccepted: true });
      },
      error: (err) => {
        console.error('Failed to accept AI instructions:', err);
        this.error = err.error?.error || 'Failed to apply instructions. Please try again.';
        this.acceptingInstructions = false;
      }
    });
  }

  onClose() {
    this.dialogRef.close();
  }
}
